/**
 * Recurring Expense Helper Functions
 * 
 * Pure helper functions extracted from RecurringExpenseService
 * for better testability and readability.
 */

import type { RecurringFrequency } from '../types';
import { getSupabase } from '../../database';

/**
 * Calculate the next due date based on frequency
 * 
 * @param currentDueDate - Current due date (YYYY-MM-DD format)
 * @param frequency - How often the expense recurs
 * @returns Next due date (YYYY-MM-DD format)
 * 
 * @example
 * ```ts
 * calculateNextDueDate('2024-01-15', 'monthly') // '2024-02-15'
 * calculateNextDueDate('2024-01-31', 'monthly') // '2024-02-29' (handles month-end)
 * ```
 */
export function calculateNextDueDate(
    currentDueDate: string,
    frequency: RecurringFrequency
): string {
    // Parse as UTC to avoid timezone issues
    const [year, month, day] = currentDueDate.split('-').map(Number);
    const current = new Date(Date.UTC(year, month - 1, day));
    let next: Date;

    switch (frequency) {
        case 'daily':
            next = new Date(current);
            next.setUTCDate(current.getUTCDate() + 1);
            break;

        case 'weekly':
            next = new Date(current);
            next.setUTCDate(current.getUTCDate() + 7);
            break;

        case 'monthly':
            next = new Date(current);
            const targetMonth = current.getUTCMonth() + 1;
            next.setUTCMonth(targetMonth);

            // Handle month-end edge case:
            // If we started on Jan 31 and move to Feb, we get Mar 3 (or Feb 28/29)
            // Reset to last day of target month
            if (next.getUTCDate() !== current.getUTCDate()) {
                next.setUTCDate(0); // Go to last day of previous month
            }
            break;

        case 'yearly':
            next = new Date(current);
            next.setUTCFullYear(current.getUTCFullYear() + 1);

            // Handle leap year edge case (Feb 29 → Feb 28)
            if (next.getUTCDate() !== current.getUTCDate()) {
                next.setUTCDate(0);
            }
            break;

        default:
            throw new Error(`Unknown frequency: ${frequency}`);
    }

    return next.toISOString().split('T')[0];
}

/**
 * Check if a recurring expense should be generated
 * 
 * @param template - Recurring expense template
 * @param today - Current date (YYYY-MM-DD)
 * @returns True if expense should be generated
 */
export function shouldGenerateExpense(
    template: { next_due_date: string; end_date: string | null; is_active: boolean },
    today: string
): boolean {
    // Must be active
    if (!template.is_active) return false;

    // Must be due (next_due_date <= today)
    if (template.next_due_date > today) return false;

    // Must not be expired (end_date check)
    if (template.end_date && template.next_due_date > template.end_date) {
        return false;
    }

    return true;
}

/**
 * Create an expense from a recurring template
 * 
 * @param template - Recurring expense template data
 * @param categoryName - Category display name (for legacy field)
 * @returns Created expense data
 */
export async function createExpenseFromTemplate(template: {
    id: string;
    user_id: string;
    amount: number;
    currency: string;
    category_id: string | null;
    description: string | null;
    next_due_date: string;
}, categoryName: string): Promise<void> {
    const supabase = getSupabase();

    const { error } = await supabase
        .from('expenses')
        .insert({
            user_id: template.user_id,
            amount: template.amount,
            currency: template.currency,
            category: categoryName, // Legacy field required
            category_id: template.category_id,
            description: template.description,
            date: template.next_due_date,
            recurring_template_id: template.id,
            is_auto_generated: true,
        });

    if (error) {
        throw new Error(`Failed to create expense from template: ${error.message}`);
    }
}

/**
 * Fetch category name by ID (for legacy field support)
 * 
 * @param categoryId - Category ID
 * @returns Category name or 'Other' if not found
 */
export async function getCategoryName(categoryId: string | null): Promise<string> {
    if (!categoryId) return 'Other';

    const supabase = getSupabase();

    const { data: category } = await supabase
        .from('user_categories')
        .select('custom_name')
        .eq('id', categoryId)
        .maybeSingle();

    return category?.custom_name || 'Other';
}

/**
 * Check if an expense already exists for a template and date
 * 
 * @param userId - User ID
 * @param templateId - Recurring template ID
 * @param date - Expense date (YYYY-MM-DD)
 * @returns True if expense already exists
 */
export async function expenseAlreadyExists(
    userId: string,
    templateId: string,
    date: string
): Promise<boolean> {
    const supabase = getSupabase();

    const { data } = await supabase
        .from('expenses')
        .select('id')
        .eq('user_id', userId)
        .eq('recurring_template_id', templateId)
        .eq('date', date)
        .maybeSingle();

    return !!data;
}

/**
 * Update the next due date for a recurring template
 * 
 * @param templateId - Recurring template ID
 * @param nextDueDate - Next due date (YYYY-MM-DD)
 */
export async function updateNextDueDate(
    templateId: string,
    nextDueDate: string
): Promise<void> {
    const supabase = getSupabase();

    const { error } = await supabase
        .from('recurring_expenses')
        .update({ next_due_date: nextDueDate })
        .eq('id', templateId);

    if (error) {
        throw new Error(`Failed to update next due date: ${error.message}`);
    }
}

/**
 * Deactivate an expired recurring template
 * 
 * @param templateId - Recurring template ID
 */
export async function deactivateTemplate(templateId: string): Promise<void> {
    const supabase = getSupabase();

    const { error } = await supabase
        .from('recurring_expenses')
        .update({ is_active: false })
        .eq('id', templateId);

    if (error) {
        throw new Error(`Failed to deactivate template: ${error.message}`);
    }
}
